import { useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useGameData } from './useGameData';
import { usePlayerStats } from './usePlayerStats';
import { useCurrentChallenge } from './useCurrentChallenge';
import { useChallengeDetail } from './useChallengeDetail';
import type { PlayerStatsResponse, H2HStatsResponse } from '@/services/api-client';
import type { PlayerStats } from '@/components/GameChallengePopup/types';

export interface GamePopupData {
  game: ReturnType<typeof useGameData>['game'];
  currentChallenge: ReturnType<typeof useCurrentChallenge>['currentChallenge'];
  playerStats: PlayerStats | null;
  isCurrentPeriod: boolean;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  refetch: () => Promise<void>;
}

/**
 * Composite hook that combines game data, current challenge, and player stats
 * @param gameId - The ID of the game to fetch data for
 * @param challengeId - The ID of the specific challenge to fetch data for (optional)
 * @returns Combined data needed for the game popup
 */
export function useGamePopupData(gameId: string | null, challengeId: string | null = null): GamePopupData {
  const { isSignedIn } = useAuth();
  
  // Fetch game data
  const { 
    game, 
    loading: gameLoading, 
    error: gameError, 
    refetch: refetchGame 
  } = useGameData(gameId);
  
  // Fetch challenges and determine current one
  const { 
    currentChallenge, 
    loading: challengeLoading, 
    error: challengeError, 
    refetch: refetchChallenges,
    isCurrentPeriod: checkIsCurrentPeriod
  } = useCurrentChallenge(gameId, game);
  
  // Fetch player stats (only if authenticated)
  const { 
    stats, 
    loading: statsLoading, 
    error: statsError, 
    refetch: refetchStats 
  } = usePlayerStats(gameId, game);

  // Fetch challenge details (for averageSolve statistic)
  // Use the provided challengeId if available, otherwise fall back to currentChallenge
  const targetChallengeId = challengeId || currentChallenge?.id?.toString() || null;
  const {
    challengeDetail,
    loading: challengeDetailLoading,
    error: challengeDetailError,
    refetch: refetchChallengeDetail
  } = useChallengeDetail(gameId, targetChallengeId);

  // Transform the stats to the simplified PlayerStats format
  const playerStats = useMemo<PlayerStats | null>(() => {
    // If we have challenge detail data, include averageSolve even if user has no stats
    if (!stats && challengeDetail && game?.challengeType === 'daily') {
      return {
        currentStreak: 0,
        averageScore: 0,
        successRate: 0,
        averageSolve: challengeDetail.averageSolve,
      };
    }
    
    if (!stats) return null;

    if ('activeGames' in stats.stats) {
      // H2H stats
      const h2hStats = stats as H2HStatsResponse;
      return {
        activeGames: h2hStats.stats.activeGames,
        winPercentage: h2hStats.stats.winPercentage,
        wins: h2hStats.stats.wins,
        losses: h2hStats.stats.losses,
        averageGamesPlayed: h2hStats.stats.averageGamesPlayed,
      };
    } else {
      // Regular challenge stats
      const regularStats = stats as PlayerStatsResponse;
      return {
        currentStreak: regularStats.stats.currentStreak,
        averageScore: regularStats.stats.averageScore,
        successRate: regularStats.stats.successRate,
        // averageSolve now comes from challengeDetail instead of player stats
        averageSolve: challengeDetail?.averageSolve,
      };
    }
  }, [stats, challengeDetail, game]);

  // Determine if the SELECTED challenge is in the current period
  // Bug fix: Previously checked currentChallenge (game's most recent), but when viewing
  // archive challenges, we need to check the selected challenge's date instead
  const isCurrentPeriod = useMemo(() => {
    // If we have challengeDetail (selected challenge), use its publishDate
    if (challengeDetail?.publishDate) {
      const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
      const challengeDate = challengeDetail.publishDate.split('T')[0];
      return challengeDate === today;
    }
    // Fallback to currentChallenge for home page (no specific challenge selected)
    if (!currentChallenge) return false;
    return checkIsCurrentPeriod(currentChallenge);
  }, [challengeDetail, currentChallenge, checkIsCurrentPeriod]);

  // Combined loading state
  const loading = gameLoading || challengeLoading || (isSignedIn && statsLoading) || challengeDetailLoading;

  // Combined error (prioritize game error, then challenge error, then stats error, then challenge detail error)
  const error = gameError || challengeError || (isSignedIn ? statsError : null) || challengeDetailError;

  // Combined refetch function
  const refetch = async () => {
    await Promise.all([
      refetchGame(),
      refetchChallenges(),
      isSignedIn ? refetchStats() : Promise.resolve(),
      refetchChallengeDetail(),
    ]);
  };

  return {
    game,
    currentChallenge,
    playerStats, // Always return playerStats, which may include just averageSolve for non-players
    isCurrentPeriod,
    loading,
    error,
    isAuthenticated: isSignedIn,
    refetch,
  };
}